import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatPrice, planFor } from '@/lib/billing';
import { useBasePath } from '@/lib/config/nav-helpers';

interface SubscriptionStatus {
  plan: string;
  status: string;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
}

interface RecapInsight {
  kind: string;
  title: string;
  detail: string;
  stat: string | null;
  statLabel: string | null;
}

interface TrialRecapResponse {
  recap: {
    mode: 'rich' | 'thin';
    hero: RecapInsight | null;
    insights: RecapInsight[];
    stats: {
      clientsTracked: number;
      dealsScored: number;
      meetingsAnalyzed: number;
      signalsSurfaced: number;
      integrationsConnected: number;
    };
    openLoops: string[];
    month6: string;
  };
  trial: {
    trialEndsAt: string;
    trialDaysRemaining: number;
    extended: boolean;
    canExtend: boolean;
  };
}

const DISMISS_KEY = 'wp-trial-recap-dismissed';

function dismissedToday(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === new Date().toISOString().slice(0, 10);
  } catch {
    return false;
  }
}

/**
 * "What we've learned" — trial-expiry conversion modal. Shows once per day
 * during the final 3 days of a trial. Rich mode leads with the account's own
 * ML insights; thin mode (not enough data) offers integrations + a one-time
 * 7-day extension instead.
 */
export function TrialRecapModal() {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(dismissedToday);
  const [extendedMsg, setExtendedMsg] = useState<string | null>(null);

  const { data: sub } = useQuery({
    queryKey: ['billing-subscription-status'],
    queryFn: () => api<SubscriptionStatus>('/v1/billing/subscription'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const eligible =
    !!sub &&
    sub.plan === 'trial' &&
    sub.status === 'trialing' &&
    sub.trialDaysRemaining !== null &&
    sub.trialDaysRemaining >= 0 &&
    sub.trialDaysRemaining <= 3;

  const { data } = useQuery({
    queryKey: ['trial-recap'],
    queryFn: () => api<TrialRecapResponse>('/v1/trial/recap'),
    enabled: eligible && !dismissed,
    staleTime: 60 * 60_000,
    retry: false,
  });

  const extend = useMutation({
    mutationFn: () => api<{ ok: boolean; trialEndsAt: string; extendedDays: number }>(
      '/v1/trial/extend',
      { method: 'POST' },
    ),
    onSuccess: (res) => {
      setExtendedMsg(`Done — your trial now runs through ${new Date(res.trialEndsAt).toLocaleDateString()}.`);
      queryClient.invalidateQueries({ queryKey: ['billing-subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['trial-recap'] });
    },
  });

  if (!eligible || dismissed || !data) return null;

  const { recap, trial } = data;
  const isRich = recap.mode === 'rich' && recap.hero;
  const daysLeft = trial.trialDaysRemaining;
  const dayWord = daysLeft === 0 ? 'today' : daysLeft === 1 ? 'tomorrow' : `in ${daysLeft} days`;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10));
    } catch { /* private mode */ }
    setDismissed(true);
  };

  const statChips = [
    { label: 'clients tracked', value: recap.stats.clientsTracked },
    { label: 'deals scored', value: recap.stats.dealsScored },
    { label: 'meetings analyzed', value: recap.stats.meetingsAnalyzed },
    { label: 'integrations live', value: recap.stats.integrationsConnected },
  ].filter((s) => s.value > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={dismiss} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="What Waypoint has learned"
        className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-border bg-surface-1 shadow-2xl"
      >
        <div className="px-6 pt-5 pb-4 border-b border-border">
          <div className="text-2xs font-semibold uppercase tracking-widest text-primary">
            What we've learned · trial ends {dayWord}
          </div>
          <h2 className="mt-2 font-serif text-xl font-bold text-ink">
            {isRich ? 'Your data has been talking.' : "We've barely met."}
          </h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          {isRich ? (
            <>
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                {recap.hero!.stat && (
                  <div>
                    <span className="font-mono text-3xl font-bold text-primary">{recap.hero!.stat}</span>
                    {recap.hero!.statLabel && (
                      <span className="ml-2 text-2xs uppercase tracking-wider text-ink-dim">
                        {recap.hero!.statLabel}
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-2 text-sm font-semibold text-ink">{recap.hero!.title}</div>
                <p className="mt-1 text-xs leading-relaxed text-ink-muted">{recap.hero!.detail}</p>
              </div>

              {recap.insights.map((i) => (
                <div key={i.title} className="flex gap-3">
                  {i.stat && (
                    <div className="w-20 shrink-0">
                      <div className="font-mono text-base font-bold text-primary leading-tight">{i.stat}</div>
                      {i.statLabel && (
                        <div className="text-3xs uppercase tracking-wide leading-tight text-ink-dim wrap-break-word">{i.statLabel}</div>
                      )}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-ink">{i.title}</div>
                    <p className="mt-0.5 text-2xs leading-relaxed text-ink-muted">{i.detail}</p>
                  </div>
                </div>
              ))}

              {statChips.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {statChips.map((s) => (
                    <span
                      key={s.label}
                      className="rounded-full border border-border bg-surface-0 px-2.5 py-1 text-2xs text-ink-dim"
                    >
                      <span className="font-mono font-semibold text-ink">{s.value}</span> {s.label}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-sm leading-relaxed text-ink-muted">
                Waypoint's intelligence layer needs data to work with — and most of yours isn't
                connected yet. Here's what's waiting:
              </p>
              <ul className="space-y-2">
                {recap.openLoops.map((l) => (
                  <li key={l} className="flex gap-2 text-xs leading-relaxed text-ink-muted">
                    <span className="text-success">·</span>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <p className="text-xs leading-relaxed text-ink-dim border-t border-border pt-3">{recap.month6}</p>

          {isRich && (
            <p className="font-serif text-sm font-bold text-ink">
              If you leave, this gets deleted. If you stay, it compounds.
            </p>
          )}

          {extendedMsg && <p className="text-xs font-semibold text-success">{extendedMsg}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
          <button onClick={dismiss} className="text-2xs text-ink-dim hover:text-ink-muted">
            Remind me later
          </button>
          <div className="flex items-center gap-2">
            {!isRich && trial.canExtend && !extendedMsg && (
              <button
                onClick={() => extend.mutate()}
                disabled={extend.isPending}
                className="rounded-lg border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
              >
                {extend.isPending ? 'Extending…' : 'Extend trial 7 days'}
              </button>
            )}
            <button
              onClick={() => {
                dismiss();
                navigate(
                  isRich
                    ? `${basePath}/settings?tab=billing`
                    : `${basePath}/settings?tab=integrations`,
                );
              }}
              className="rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-white shadow-xs hover:opacity-90"
            >
              {isRich ? `Keep my intelligence — from ${formatPrice(planFor('owner').monthlyCents)}/mo` : 'Connect my data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
